
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Send, AlertCircle, RefreshCw, Check } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export function InfradigitalDirectTest() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState("12345");
  const [amount, setAmount] = useState("10.00");
  const [notes, setNotes] = useState("Test bill from direct Infradigital function");
  const [successes, setSuccesses] = useState<{original: boolean, new: boolean}>({original: false, new: false});
  const { toast } = useToast();

  const handleTestDirectCall = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    setSuccesses(prev => ({...prev, original: false}));
    
    try {
      // Create test data for the bill
      const testData = {
        "create_by": "test@example.com",
        "biller_code": "60000",
        "bill_key": schoolId.trim(),
        "account_code": "MANDIRI_IDN",
        "bill_component_name": `TEST_${Date.now()}`,
        "expiry_date": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        "due_date": new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        "active_date": new Date().toISOString(),
        "amount": parseFloat(amount),
        "amount_currency": "IDR",
        "penalty_amount": 0,
        "batch_id": new Date().toISOString(),
        "notes": notes
      };

      console.log('📤 Sending test bill data to direct Infradigital function:', JSON.stringify({ data: testData }, null, 2));
      
      // Call the original infradigital function directly
      const { data, error } = await supabase.functions.invoke('infradigital', {
        body: { data: testData }
      });

      if (error) {
        console.error('❌ Direct Infradigital test error:', error);
        setError(error.message || 'Unknown error occurred');
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || 'Unknown error occurred',
        });
        setLoading(false);
        return;
      }
      
      setResponse(data);
      setSuccesses(prev => ({...prev, original: true}));
      
      toast({
        title: "Success",
        description: "Test bill creation successful using direct Infradigital function",
      });
    } catch (error: any) {
      console.error('❌ Direct Infradigital test error:', error);
      setError(error.message || "Failed to send test bill request");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send test bill request",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestNewCall = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    setSuccesses(prev => ({...prev, new: false}));
    
    try {
      // Create test data for the bill
      const testData = {
        "create_by": "test@example.com",
        "biller_code": "60000",
        "bill_key": schoolId.trim(),
        "account_code": "MANDIRI_IDN",
        "bill_component_name": `TEST_${Date.now()}`,
        "expiry_date": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        "due_date": new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        "active_date": new Date().toISOString(),
        "amount": parseFloat(amount),
        "amount_currency": "IDR",
        "penalty_amount": 0,
        "batch_id": new Date().toISOString(),
        "notes": notes
      };

      console.log('📤 Sending test bill data to create-bill-component function:', JSON.stringify({ data: testData }, null, 2));
      
      // Call the newer create-bill-component function
      const { data, error } = await supabase.functions.invoke('create-bill-component', {
        body: { data: testData }
      });

      if (error) {
        console.error('❌ Create-bill-component test error:', error);
        setError(error.message || 'Unknown error occurred');
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || 'Unknown error occurred',
        });
        setLoading(false);
        return;
      }
      
      setResponse(data);
      setSuccesses(prev => ({...prev, new: true}));
      
      toast({
        title: "Success",
        description: "Test bill creation successful using create-bill-component function",
      });
    } catch (error: any) {
      console.error('❌ Create-bill-component test error:', error);
      setError(error.message || "Failed to send test bill request");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send test bill request",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Compare Infradigital API Methods</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schoolId">School ID</Label>
                <Input 
                  id="schoolId"
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  placeholder="School ID"
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input 
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount"
                  type="number"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea 
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bill notes"
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={handleTestDirectCall}
              disabled={loading}
              variant="default"
              className={successes.original ? "bg-green-600 hover:bg-green-700" : ""}
            >
              {loading ? "Sending..." : "Test Original Infradigital Function"} 
              {successes.original ? <Check className="ml-2 h-4 w-4" /> : <Send className="ml-2 h-4 w-4" />}
            </Button>
            
            <Button 
              onClick={handleTestNewCall}
              disabled={loading}
              variant="outline"
              className={successes.new ? "bg-green-600 text-white hover:bg-green-700" : ""}
            >
              {loading ? "Sending..." : "Test New create-bill-component Function"}
              {successes.new ? <Check className="ml-2 h-4 w-4" /> : <RefreshCw className="ml-2 h-4 w-4" />}
            </Button>
          </div>

          <div className="text-xs text-gray-500 mt-1">
            <Badge variant="outline" className="mr-2">Original</Badge> Uses all headers including Authorization
            <br />
            <Badge variant="outline" className="mr-2">New</Badge> Uses only Content-Type and x-idn-web-proxy-client-token
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="mt-2">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {response && (
            <div className="mt-4">
              <h3 className="font-semibold mb-2">API Response:</h3>
              <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
